import type {
  CreatePrinterRequest,
  PrintLayoutConfig,
  StorePrinter,
  UpdatePrinterRequest,
} from "@medicorepay/shared/types";
import apiClient from "../lib/api-client";

export const printerService = {
  /**
   * Get all printers for a specific store
   */
  async getByStore(storeId: string): Promise<StorePrinter[]> {
    const response = await apiClient.get(`/printer-config/store/${storeId}`);
    return response.data;
  },

  /**
   * Get all printers across all tenant stores
   */
  async getByTenant(): Promise<StorePrinter[]> {
    const response = await apiClient.get("/printer-config/tenant");
    return response.data;
  },

  /**
   * Get the default billing printer for a store
   */
  async getDefaultForStore(
    storeId: string,
    purpose: string = "BILLING",
  ): Promise<StorePrinter | null> {
    const response = await apiClient.get(
      `/printer-config/store/${storeId}/default`,
      { params: { purpose } },
    );
    return response.data;
  },

  /**
   * Get a single printer config by ID
   */
  async getById(id: string): Promise<StorePrinter> {
    const response = await apiClient.get(`/printer-config/${id}`);
    return response.data;
  },

  /**
   * Get layout presets for a printer type
   */
  async getPresets(printerType: string): Promise<PrintLayoutConfig> {
    const response = await apiClient.get(
      `/printer-config/presets/${printerType}`,
    );
    return response.data;
  },

  /**
   * Create a new printer config
   */
  async create(data: CreatePrinterRequest): Promise<StorePrinter> {
    const response = await apiClient.post("/printer-config", data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  /**
   * Update a printer config
   */
  async update(id: string, data: UpdatePrinterRequest): Promise<StorePrinter> {
    const response = await apiClient.patch(`/printer-config/${id}`, data, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
    return response.data;
  },

  /**
   * Delete a printer config
   */
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/printer-config/${id}`, {
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
  },
};
